import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { LogOut, User, Settings, Menu, X, GaugeCircle } from "lucide-react";

export function Navigation() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      setIsLoading(true);
      try {
        const { data } = await supabase.auth.getSession();
        setSession(data.session);

        if (data.session?.user) {
          const role = data.session.user.user_metadata?.role;
          if (role === 'admin') {
            setIsAdmin(true);
          } else {
            const { data: isAdminData } = await supabase.rpc('is_admin');
            setIsAdmin(!!isAdminData);
            
            if (isAdminData) {
              await supabase.auth.updateUser({
                data: { role: 'admin' }
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      
      if (session?.user) {
        const role = session.user.user_metadata?.role;
        if (role === 'admin') {
          setIsAdmin(true);
        } else {
          try {
            const { data: isAdminData } = await supabase.rpc('is_admin');
            setIsAdmin(!!isAdminData);
            
            if (isAdminData) {
              supabase.auth.updateUser({
                data: { role: 'admin' }
              });
            }
          } catch (error) {
            console.error("Error checking admin status:", error);
            setIsAdmin(false);
          }
        }
      } else {
        setIsAdmin(false);
      }
    });

    return () => {
      setMobileMenuOpen(false);
      subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <nav className="bg-white border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="text-xl font-bold text-primary">
              Spark Quotes
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {session ? (
              <>
                {!isLoading && (
                  <>
                    <Link to="/account/subscription">
                      <Button variant="ghost" size="sm" className="gap-2">
                        <GaugeCircle size={16} />
                        <span>Subscription</span>
                      </Button>
                    </Link>
                    
                    <Link to="/account/profile">
                      <Button variant="ghost" size="sm" className="gap-2">
                        <User size={16} />
                        <span>Profile</span>
                      </Button>
                    </Link>
                    
                    {isAdmin && (
                      <Link to="/settings">
                        <Button variant="ghost" size="sm" className="gap-2">
                          <Settings size={16} />
                          <span>Admin</span>
                        </Button>
                      </Link>
                    )}
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleLogout}
                      className="gap-2"
                    >
                      <LogOut size={16} />
                      <span>Log Out</span>
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/verify">
                  <Button variant="ghost">Get Started</Button>
                </Link>
                <Link to="/verify">
                  <Button variant="outline">Admin Login</Button>
                </Link>
              </>
            )}
          </div>

          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
            >
              {mobileMenuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t py-4">
            <div className="flex flex-col space-y-2">
              {session ? (
                <>
                  {!isLoading && (
                    <>
                      <Link to="/account/subscription" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                          <GaugeCircle size={16} />
                          <span>Subscription</span>
                        </Button>
                      </Link>
                      
                      <Link to="/account/profile" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                          <User size={16} />
                          <span>Profile</span>
                        </Button>
                      </Link>
                      
                      {isAdmin && (
                        <Link to="/settings" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="ghost" size="sm" className="w-full justify-start gap-2">
                            <Settings size={16} />
                            <span>Admin</span>
                          </Button>
                        </Link>
                      )}
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full justify-start gap-2"
                      >
                        <LogOut size={16} />
                        <span>Log Out</span>
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Link to="/verify" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start">
                      Get Started
                    </Button>
                  </Link>
                  <Link to="/verify" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full justify-start">
                      Admin Login
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
